import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { getFinancialReports, getAllAccounts, getFullFinancialHistory, ACCOUNT_NATURES } from "../firebase/database";
import { theme } from "../utils/theme";
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addDays, isSameDay, parseISO, startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns";

const C = theme.colors;

export default function FinancialReports() {
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("weekly");
  const [accounts, setAccounts] = useState([]);
  const [data, setData] = useState({ invoices: [], expenses: [], jvs: [], accounts: [] });
  
  const [dateRange, setDateRange] = useState({
    start: format(startOfWeek(new Date(), { weekStartsOn: 0 }), "yyyy-MM-dd"),
    end: format(endOfWeek(new Date(), { weekStartsOn: 0 }), "yyyy-MM-dd")
  });
  
  // Ledger specific state
  const [selectedAccountId, setSelectedAccountId] = useState("");
  
  // Day Book specific state
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = parseISO(dateStr);
      return format(date, "dd/MM/yy");
    } catch (e) {
      return dateStr;
    }
  };

  const setPeriod = (type) => {
    const now = new Date();
    let start, end;

    if (type === "today") {
      start = format(now, "yyyy-MM-dd");
      end = format(now, "yyyy-MM-dd");
    } else if (type === "weekly") {
      start = format(startOfWeek(now, { weekStartsOn: 0 }), "yyyy-MM-dd");
      end = format(endOfWeek(now, { weekStartsOn: 0 }), "yyyy-MM-dd");
    } else if (type === "monthly") {
      start = format(startOfMonth(now), "yyyy-MM-dd");
      end = format(endOfMonth(now), "yyyy-MM-dd");
    } else if (type === "annual") {
      const currentMonth = now.getMonth();
      let startYear = now.getFullYear();
      if (currentMonth < 3) startYear -= 1;
      start = `${startYear}-04-01`;
      end = `${startYear + 1}-03-31`;
    }
    
    if (start && end) {
      setDateRange({ start, end });
      if (type === "today") setSelectedDate(start);
    }
  };

  const activePeriod = useMemo(() => {
    const now = new Date();
    const tS = format(now, "yyyy-MM-dd");
    const wS = format(startOfWeek(now, { weekStartsOn: 0 }), "yyyy-MM-dd");
    const wE = format(endOfWeek(now, { weekStartsOn: 0 }), "yyyy-MM-dd");
    const mS = format(startOfMonth(now), "yyyy-MM-dd");
    const mE = format(endOfMonth(now), "yyyy-MM-dd");
    
    const curM = now.getMonth();
    let sY = now.getFullYear();
    if (curM < 3) sY -= 1;
    const aS = `${sY}-04-01`;
    const aE = `${sY + 1}-03-31`;

    if (dateRange.start === tS && dateRange.end === tS) return "today";
    if (dateRange.start === wS && dateRange.end === wE) return "weekly";
    if (dateRange.start === mS && dateRange.end === mE) return "monthly";
    if (dateRange.start === aS && dateRange.end === aE) return "annual";
    return "custom";
  }, [dateRange]);

  useEffect(() => {
    loadData();
    // Sync Day Book selected date if it's a single day range
    if (dateRange.start === dateRange.end) {
      setSelectedDate(dateRange.start);
    }
  }, [dateRange]);

  // --- LEDGER ENTRIES LOGIC ---
  const ledgerEntries = useMemo(() => {
    if (!selectedAccountId) return [];
    const entries = [];
    let balance = 0;

    // 1. From Invoices (Paid)
    data.invoices.filter(inv => inv.status === "paid" && (inv.paymentAccountId === selectedAccountId)).forEach(inv => {
      entries.push({
        date: inv.date,
        ref: inv.invoiceNo,
        particulars: `To ${inv.customerName}`,
        dr: inv.totalAmount,
        cr: 0
      });
    });

    // 2. From Expenses
    data.expenses.filter(exp => exp.accountId === selectedAccountId || exp.paymentAccountId === selectedAccountId).forEach(exp => {
      const isExpenseAcc = exp.accountId === selectedAccountId;
      const isPaymentAcc = exp.paymentAccountId === selectedAccountId;
      
      if (isExpenseAcc) {
        entries.push({
          date: exp.date,
          ref: exp.voucherNo,
          particulars: `By Payment`,
          dr: exp.amount,
          cr: 0
        });
      }
      if (isPaymentAcc) {
        entries.push({
          date: exp.date,
          ref: exp.voucherNo,
          particulars: `To ${exp.accountName || 'Expense'}`,
          dr: 0,
          cr: exp.amount
        });
      }
    });

    // 3. From JVs
    data.jvs.forEach(jv => {
      jv.entries.forEach(e => {
        if (e.accountId === selectedAccountId) {
          entries.push({
            date: jv.date,
            ref: jv.jvNo,
            particulars: jv.narration,
            dr: e.dr || 0,
            cr: e.cr || 0
          });
        }
      });
    });

    return entries.sort((a,b) => a.date.localeCompare(b.date)).map(e => {
      balance += (e.dr - e.cr);
      return { ...e, balance };
    });
  }, [data, selectedAccountId]);

  // --- ITEM WISE SALES LOGIC ---
  const itemWiseSales = useMemo(() => {
    const itemMap = {};
    
    data.invoices.forEach(inv => {
        (inv.items || []).forEach(item => {
            const id = item.itemId || item.id || item.itemName || item.name;
            const name = item.itemName || item.name || "Unknown Item";
            
            if (!itemMap[id]) {
                itemMap[id] = { name: name, quantity: 0, amount: 0 };
            }
            itemMap[id].quantity += (parseFloat(item.quantity) || 0);
            itemMap[id].amount += (parseFloat(item.total) || parseFloat(item.amount) || 0);
        });
    });
    
    return Object.values(itemMap).sort((a,b) => b.amount - a.amount);
  }, [data]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [reportData, allAccounts] = await Promise.all([
        getFinancialReports(dateRange.start, dateRange.end),
        getAllAccounts()
      ]);
      setData(reportData);
      setAccounts(allAccounts);
      if (allAccounts.length > 0 && !selectedAccountId) {
        setSelectedAccountId(allAccounts[0].id);
      }
    } catch (err) {
      console.error("Error loading report data:", err);
    } finally {
      setLoading(false);
    }
  };

  // --- WEEKLY REPORT LOGIC ---
  const weeklyReport = useMemo(() => {
    const days = eachDayOfInterval({
      start: parseISO(dateRange.start),
      end: parseISO(dateRange.end)
    });

    let weeklyTotal = { received: 0, pending: 0, total: 0, expense: 0 };
    
    const rows = days.map(day => {
      const dateStr = format(day, "yyyy-MM-dd");
      
      const dayInvoices = data.invoices.filter(inv => inv.date === dateStr);
      const dayExpenses = data.expenses.filter(exp => exp.date === dateStr);
      
      const received = dayInvoices.filter(inv => inv.status === "paid").reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
      const pending = dayInvoices.filter(inv => inv.status !== "paid").reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
      const total = received + pending;
      const expense = dayExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
      
      weeklyTotal.received += received;
      weeklyTotal.pending += pending;
      weeklyTotal.total += total;
      weeklyTotal.expense += expense;
      
      return {
        date: dateStr,
        dayName: format(day, "EEE"),
        received,
        pending,
        total,
        expense
      };
    });
    
    return { rows, total: weeklyTotal };
  }, [data, dateRange]);

  // --- DAY BOOK LOGIC ---
  const dayBookEntries = useMemo(() => {
    const entries = [];
    
    data.invoices.filter(inv => inv.date === selectedDate).forEach(inv => {
      entries.push({
        type: "Invoice",
        ref: inv.invoiceNo,
        particulars: `To ${inv.customerName}`,
        amount: inv.totalAmount,
        status: inv.status
      });
    });
    
    data.expenses.filter(exp => exp.date === selectedDate).forEach(exp => {
      entries.push({
        type: "Expense",
        ref: exp.voucherNo,
        particulars: `By ${exp.accountName || 'Expense'}`,
        amount: exp.amount,
        status: "paid"
      });
    });

    data.jvs.filter(jv => jv.date === selectedDate).forEach(jv => {
        entries.push({
          type: "JV",
          ref: jv.jvNo,
          particulars: jv.narration,
          amount: jv.totalAmount,
          status: "posted"
        });
      });
    
    return entries;
  }, [data, selectedDate]);

  // --- CASH BOOK LOGIC ---
  const cashBookEntries = useMemo(() => {
    const cashBankAccounts = accounts.filter(a => a.nature === "Cash" || a.nature === "Bank").map(a => a.id);
    const entries = [];
    
    // Invoices are usually cash/bank in if paid
    data.invoices.filter(inv => inv.status === "paid" && cashBankAccounts.includes(inv.paymentAccountId)).forEach(inv => {
      entries.push({
        date: inv.date,
        particulars: `Invoice ${inv.invoiceNo} - ${inv.customerName}`,
        in: inv.totalAmount,
        out: 0
      });
    });
    
    // Expenses are cash/bank out
    data.expenses.filter(exp => cashBankAccounts.includes(exp.paymentAccountId)).forEach(exp => {
        entries.push({
          date: exp.date,
          particulars: `Voucher ${exp.voucherNo} - ${exp.accountName}`,
          in: 0,
          out: exp.amount
        });
      });

    // JVs might involve cash/bank
    data.jvs.forEach(jv => {
        jv.entries.forEach(entry => {
            if (cashBankAccounts.includes(entry.accountId)) {
                entries.push({
                    date: jv.date,
                    particulars: `JV ${jv.jvNo} - ${jv.narration}`,
                    in: entry.dr || 0,
                    out: entry.cr || 0
                });
            }
        });
    });
    
    return entries.sort((a,b) => a.date.localeCompare(b.date));
  }, [data, accounts]);

  // --- PROFIT & LOSS LOGIC ---
  const profitAndLoss = useMemo(() => {
    const incomeAccounts = accounts.filter(a => a.nature === "Income").map(a => a.id);
    const expenseAccounts = accounts.filter(a => a.nature === "Expense").map(a => a.id);
    
    let totalIncome = 0;
    let totalExpense = 0;
    
    const incomeDetails = [];
    const expenseDetails = [];

    // 1. From Invoices (Paid)
    data.invoices.filter(inv => inv.status === "paid").forEach(inv => {
        totalIncome += (inv.totalAmount || 0);
    });
    if (totalIncome > 0) {
        incomeDetails.push({ name: "Invoice Income", amount: totalIncome });
    }

    // 2. From Expenses
    data.expenses.forEach(exp => {
        totalExpense += (exp.amount || 0);
        const existing = expenseDetails.find(e => e.name === exp.accountName);
        if (existing) existing.amount += exp.amount;
        else expenseDetails.push({ name: exp.accountName || "General Expense", amount: exp.amount });
    });

    // 3. From JVs
    data.jvs.forEach(jv => {
        jv.entries.forEach(entry => {
            if (incomeAccounts.includes(entry.accountId)) {
                // Cr income is +ve, Dr income is -ve
                const amt = (entry.cr || 0) - (entry.dr || 0);
                totalIncome += amt;
                const acc = accounts.find(a => a.id === entry.accountId);
                incomeDetails.push({ name: `JV: ${acc?.name || 'Income'}`, amount: amt });
            }
            if (expenseAccounts.includes(entry.accountId)) {
                // Dr expense is +ve, Cr expense is -ve
                const amt = (entry.dr || 0) - (entry.cr || 0);
                totalExpense += amt;
                const acc = accounts.find(a => a.id === entry.accountId);
                expenseDetails.push({ name: `JV: ${acc?.name || 'Expense'}`, amount: amt });
            }
        });
    });

    return { totalIncome, totalExpense, netProfit: totalIncome - totalExpense, incomeDetails, expenseDetails };
  }, [data, accounts]);

  // --- BALANCE SHEET LOGIC ---
  const [fullHistory, setFullHistory] = useState({ invoices: [], expenses: [], jvs: [], accounts: [] });

  useEffect(() => {
    if (tab === "balancesheet") {
        getFullFinancialHistory().then(setFullHistory);
    }
  }, [tab]);

  const balanceSheet = useMemo(() => {
    const balances = {};
    fullHistory.accounts.forEach(a => balances[a.id] = 0);

    // 1. Invoices (Debit Cash/Bank, Credit Income)
    fullHistory.invoices.filter(i => i.status === "paid").forEach(inv => {
        if (inv.paymentAccountId) {
            balances[inv.paymentAccountId] = (balances[inv.paymentAccountId] || 0) + inv.totalAmount;
        }
        // Assuming there is a default income account or we skip it for basic BS
    });

    // 2. Expenses (Credit Cash/Bank, Debit Expense)
    fullHistory.expenses.forEach(exp => {
        if (exp.paymentAccountId) {
            balances[exp.paymentAccountId] = (balances[exp.paymentAccountId] || 0) - exp.amount;
        }
        balances[exp.accountId] = (balances[exp.accountId] || 0) + exp.amount;
    });

    // 3. JVs (Dr/Cr)
    fullHistory.jvs.forEach(jv => {
        jv.entries.forEach(e => {
            balances[e.accountId] = (balances[e.accountId] || 0) + (e.dr || 0) - (e.cr || 0);
        });
    });

    const assets = [], liabilities = [], equity = [];
    
    // Calculate Net Profit
    let netProfit = 0;
    fullHistory.invoices.filter(i => i.status === "paid").forEach(i => netProfit += i.totalAmount);
    fullHistory.expenses.forEach(e => netProfit -= e.amount);
    fullHistory.jvs.forEach(jv => {
        jv.entries.forEach(e => {
            const acc = fullHistory.accounts.find(a => a.id === e.accountId);
            if (!acc) return;
            if (acc.nature === "Income") netProfit += (e.cr - e.dr);
            if (acc.nature === "Expense") netProfit -= (e.dr - e.cr);
        });
    });

    fullHistory.accounts.forEach(a => {
        const bal = balances[a.id] || 0;
        const entry = { name: a.name, balance: bal };
        
        if (a.nature === "Asset") assets.push(entry);
        else if (a.nature === "Liability") liabilities.push(entry);
        else if (a.nature === "Equity") equity.push(entry);
    });

    equity.push({ name: "Retained Earnings (Net Profit)", balance: netProfit });

    return { assets, liabilities, equity };
  }, [fullHistory]);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.muted }}>Loading reports...</div>;

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: 24, fontFamily: "DM Sans, sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Link to="/admin" style={{ color: C.muted }}><svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7" /></svg></Link>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: C.text, margin: 0 }}>Financial Reports</h1>
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div style={{ display: "flex", background: C.card, borderRadius: 8, padding: 3, border: `1px solid ${C.border}` }}>
            <button 
              onClick={() => setPeriod("today")} 
              style={{ padding: "6px 12px", border: "none", borderRadius: 6, background: activePeriod === "today" ? C.accent : "transparent", color: activePeriod === "today" ? "white" : C.muted, fontWeight: 600, cursor: "pointer" }}
            >Today</button>
            <button 
              onClick={() => setPeriod("weekly")} 
              style={{ padding: "6px 12px", border: "none", borderRadius: 6, background: activePeriod === "weekly" ? C.accent : "transparent", color: activePeriod === "weekly" ? "white" : C.muted, fontWeight: 600, cursor: "pointer" }}
            >Weekly</button>
            <button 
              onClick={() => setPeriod("monthly")} 
              style={{ padding: "6px 12px", border: "none", borderRadius: 6, background: activePeriod === "monthly" ? C.accent : "transparent", color: activePeriod === "monthly" ? "white" : C.muted, fontWeight: 600, cursor: "pointer" }}
            >Monthly</button>
            <button 
              onClick={() => setPeriod("annual")} 
              style={{ padding: "6px 12px", border: "none", borderRadius: 6, background: activePeriod === "annual" ? C.accent : "transparent", color: activePeriod === "annual" ? "white" : C.muted, fontWeight: 600, cursor: "pointer" }}
            >Annual</button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input 
              type="date" 
              value={dateRange.start} 
              onChange={e => setDateRange({...dateRange, start: e.target.value})} 
              style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.text }}
            />
            <span style={{ color: C.muted }}>to</span>
            <input 
              type="date" 
              value={dateRange.end} 
              onChange={e => setDateRange({...dateRange, end: e.target.value})} 
              style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.text }}
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24, overflowX: "auto", paddingBottom: 8 }}>
        {[
          { id: "weekly", label: "Income Expense" },
          { id: "itemsales", label: "Item Sales" },
          { id: "daybook", label: "Day Book" },
          { id: "cashbook", label: "Cash Book" },
          { id: "pl", label: "Profit & Loss" },
          { id: "balancesheet", label: "Balance Sheet" },
          { id: "ledger", label: "Ledger" }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: "10px 20px",
              borderRadius: 8,
              border: "none",
              background: tab === t.id ? C.accent : C.card,
              color: tab === t.id ? "white" : C.muted,
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap"
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* INCOME EXPENSE REPORT (formerly WEEKLY) */}
      {tab === "weekly" && (
        <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: C.dim, borderBottom: `1px solid ${C.border}` }}>
                <th style={{ padding: 12, textAlign: "left", fontSize: 13, color: C.muted }}>Date</th>
                <th style={{ padding: 12, textAlign: "right", fontSize: 13, color: C.muted }}>Income (Recv.)</th>
                <th style={{ padding: 12, textAlign: "right", fontSize: 13, color: C.muted }}>Income (Pend.)</th>
                <th style={{ padding: 12, textAlign: "right", fontSize: 13, color: C.muted }}>Total Income</th>
                <th style={{ padding: 12, textAlign: "right", fontSize: 13, color: C.muted }}>Expense</th>
                <th style={{ padding: 12, textAlign: "right", fontSize: 13, color: C.muted }}>Net</th>
              </tr>
            </thead>
            <tbody>
              {weeklyReport.rows.map(row => (
              <tr key={row.date} style={{ borderBottom: `1px solid ${C.border}`, background: row.dayName === "Sun" ? `${C.accent}05` : "transparent" }}>
                <td style={{ padding: 12 }}>
                  <div style={{ fontWeight: 600, color: C.text }}>{formatDateDisplay(row.date)}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{row.dayName}</div>
                </td>

                  <td style={{ padding: 12, textAlign: "right", color: C.green, fontWeight: 500 }}>₹{row.received.toLocaleString()}</td>
                  <td style={{ padding: 12, textAlign: "right", color: C.amber, fontWeight: 500 }}>₹{row.pending.toLocaleString()}</td>
                  <td style={{ padding: 12, textAlign: "right", fontWeight: 600, color: C.text }}>₹{row.total.toLocaleString()}</td>
                  <td style={{ padding: 12, textAlign: "right", color: C.red, fontWeight: 500 }}>₹{row.expense.toLocaleString()}</td>
                  <td style={{ padding: 12, textAlign: "right", fontWeight: 700, color: row.total - row.expense >= 0 ? C.green : C.red }}>
                    ₹{(row.total - row.expense).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: C.dim, fontWeight: 700 }}>
                <td style={{ padding: 16, color: C.text }}>TOTAL FOR PERIOD</td>
                <td style={{ padding: 16, textAlign: "right", color: C.green }}>₹{weeklyReport.total.received.toLocaleString()}</td>
                <td style={{ padding: 16, textAlign: "right", color: C.amber }}>₹{weeklyReport.total.pending.toLocaleString()}</td>
                <td style={{ padding: 16, textAlign: "right", color: C.text }}>₹{weeklyReport.total.total.toLocaleString()}</td>
                <td style={{ padding: 16, textAlign: "right", color: C.red }}>₹{weeklyReport.total.expense.toLocaleString()}</td>
                <td style={{ padding: 16, textAlign: "right", color: weeklyReport.total.total - weeklyReport.total.expense >= 0 ? C.green : C.red }}>
                  ₹{(weeklyReport.total.total - weeklyReport.total.expense).toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* ITEM SALES */}
      {tab === "itemsales" && (
        <div>
          <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                  <th style={{ padding: 12, textAlign: "left", fontSize: 13, color: C.muted }}>Item Name</th>
                  <th style={{ padding: 12, textAlign: "right", fontSize: 13, color: C.muted }}>Quantity Sold</th>
                  <th style={{ padding: 12, textAlign: "right", fontSize: 13, color: C.muted }}>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {itemWiseSales.map((item, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: 12, color: C.text, fontWeight: 600 }}>{item.name}</td>
                    <td style={{ padding: 12, textAlign: "right", color: C.text }}>{item.quantity}</td>
                    <td style={{ padding: 12, textAlign: "right", fontWeight: 700, color: C.accent }}>₹{item.amount.toLocaleString()}</td>
                  </tr>
                ))}
                {itemWiseSales.length === 0 && <tr><td colSpan="3" style={{ padding: 40, textAlign: "center", color: C.muted }}>No sales found for this period</td></tr>}
              </tbody>
              <tfoot>
                <tr style={{ background: C.bg, fontWeight: 700 }}>
                  <td style={{ padding: 12, color: C.text }}>TOTAL SALES</td>
                  <td style={{ padding: 12, textAlign: "right", color: C.text }}>{itemWiseSales.reduce((s,i) => s+i.quantity, 0)}</td>
                  <td style={{ padding: 12, textAlign: "right", color: C.accent }}>₹{itemWiseSales.reduce((s,i) => s+i.amount, 0).toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* DAY BOOK */}
      {tab === "daybook" && (
        <div>
          <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ color: C.muted }}>Select Date:</span>
            <input 
                type="date" 
                value={selectedDate} 
                onChange={e => {
                    const d = e.target.value;
                    setSelectedDate(d);
                    setDateRange({ start: d, end: d });
                }} 
                style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.text }} 
            />
          </div>
          <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: C.dim, borderBottom: `1px solid ${C.border}` }}>
                  <th style={{ padding: 12, textAlign: "left", fontSize: 13, color: C.muted }}>Type</th>
                  <th style={{ padding: 12, textAlign: "left", fontSize: 13, color: C.muted }}>Reference</th>
                  <th style={{ padding: 12, textAlign: "left", fontSize: 13, color: C.muted }}>Particulars</th>
                  <th style={{ padding: 12, textAlign: "right", fontSize: 13, color: C.muted }}>Amount</th>
                  <th style={{ padding: 12, textAlign: "center", fontSize: 13, color: C.muted }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {dayBookEntries.map((entry, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: 12, fontSize: 13, color: C.muted }}>{entry.type}</td>
                    <td style={{ padding: 12, fontWeight: 600, color: C.text }}>{entry.ref}</td>
                    <td style={{ padding: 12, color: C.text }}>{entry.particulars}</td>
                    <td style={{ padding: 12, textAlign: "right", fontWeight: 700, color: C.text }}>₹{entry.amount.toLocaleString()}</td>
                    <td style={{ padding: 12, textAlign: "center" }}>
                        <span style={{ 
                            padding: "2px 8px", 
                            borderRadius: 12, 
                            fontSize: 11, 
                            background: entry.status === "paid" ? "#D1FAE5" : "#FEE2E2",
                            color: entry.status === "paid" ? "#047857" : "#DC2626"
                        }}>{entry.status}</span>
                    </td>
                  </tr>
                ))}
                {dayBookEntries.length === 0 && <tr><td colSpan="5" style={{ padding: 40, textAlign: "center", color: C.muted }}>No transactions for this date</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CASH BOOK */}
      {tab === "cashbook" && (
        <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: C.dim, borderBottom: `1px solid ${C.border}` }}>
                  <th style={{ padding: 12, textAlign: "left", fontSize: 13, color: C.muted }}>Date</th>
                  <th style={{ padding: 12, textAlign: "left", fontSize: 13, color: C.muted }}>Particulars</th>
                  <th style={{ padding: 12, textAlign: "right", fontSize: 13, color: C.muted }}>Cash In (Dr)</th>
                  <th style={{ padding: 12, textAlign: "right", fontSize: 13, color: C.muted }}>Cash Out (Cr)</th>
                </tr>
              </thead>
              <tbody>
                {cashBookEntries.map((entry, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: 12, fontSize: 13, color: C.muted }}>{formatDateDisplay(entry.date)}</td>
                    <td style={{ padding: 12, color: C.text }}>{entry.particulars}</td>
                    <td style={{ padding: 12, textAlign: "right", color: "#059669", fontWeight: 600 }}>{entry.in > 0 ? `₹${entry.in.toLocaleString()}` : "-"}</td>
                    <td style={{ padding: 12, textAlign: "right", color: "#DC2626", fontWeight: 600 }}>{entry.out > 0 ? `₹${entry.out.toLocaleString()}` : "-"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: C.dim, fontWeight: 700 }}>
                  <td colSpan="2" style={{ padding: 12, color: C.text }}>TOTAL</td>
                  <td style={{ padding: 12, textAlign: "right", color: "#059669" }}>₹{cashBookEntries.reduce((s,e) => s+e.in, 0).toLocaleString()}</td>
                  <td style={{ padding: 12, textAlign: "right", color: "#DC2626" }}>₹{cashBookEntries.reduce((s,e) => s+e.out, 0).toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
        </div>
      )}

      {/* PROFIT & LOSS */}
      {tab === "pl" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            {/* Income Side */}
            <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: 20 }}>
                <h3 style={{ color: C.green, marginBottom: 16 }}>Income</h3>
                {profitAndLoss.incomeDetails.map((inc, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                        <span style={{ color: C.text }}>{inc.name}</span>
                        <span style={{ fontWeight: 600, color: C.text }}>₹{inc.amount.toLocaleString()}</span>
                    </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", padding: "16px 0", fontWeight: 700, fontSize: 18, color: C.green }}>
                    <span>Total Income</span>
                    <span>₹{profitAndLoss.totalIncome.toLocaleString()}</span>
                </div>
            </div>

            {/* Expense Side */}
            <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: 20 }}>
                <h3 style={{ color: C.red, marginBottom: 16 }}>Expenses</h3>
                {profitAndLoss.expenseDetails.map((exp, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                        <span style={{ color: C.text }}>{exp.name}</span>
                        <span style={{ fontWeight: 600, color: C.text }}>₹{exp.amount.toLocaleString()}</span>
                    </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", padding: "16px 0", fontWeight: 700, fontSize: 18, color: C.red }}>
                    <span>Total Expenses</span>
                    <span>₹{profitAndLoss.totalExpense.toLocaleString()}</span>
                </div>
            </div>

            {/* Summary */}
            <div style={{ gridColumn: "1 / -1", background: profitAndLoss.netProfit >= 0 ? C.greenSoft : C.redSoft, borderRadius: 12, padding: 24, textAlign: "center", border: `1px solid ${profitAndLoss.netProfit >= 0 ? C.green : C.red}22` }}>
                <div style={{ fontSize: 16, color: profitAndLoss.netProfit >= 0 ? C.green : C.red, fontWeight: 600 }}>NET {profitAndLoss.netProfit >= 0 ? "PROFIT" : "LOSS"}</div>
                <div style={{ fontSize: 36, fontWeight: 800, color: profitAndLoss.netProfit >= 0 ? C.green : C.red }}>₹{Math.abs(profitAndLoss.netProfit).toLocaleString()}</div>
            </div>
        </div>
      )}

      {/* LEDGER */}
      {tab === "ledger" && (
        <div>
            <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ color: C.muted }}>Select Account:</span>
                <select 
                    value={selectedAccountId} 
                    onChange={e => setSelectedAccountId(e.target.value)}
                    style={{ padding: "10px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.text, outline: "none" }}
                >
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.nature})</option>)}
                </select>
            </div>
            <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr style={{ background: C.dim, borderBottom: `1px solid ${C.border}` }}>
                            <th style={{ padding: 12, textAlign: "left", fontSize: 13, color: C.muted }}>Date</th>
                            <th style={{ padding: 12, textAlign: "left", fontSize: 13, color: C.muted }}>Reference</th>
                            <th style={{ padding: 12, textAlign: "left", fontSize: 13, color: C.muted }}>Particulars</th>
                            <th style={{ padding: 12, textAlign: "right", fontSize: 13, color: C.muted }}>Debit (Dr)</th>
                            <th style={{ padding: 12, textAlign: "right", fontSize: 13, color: C.muted }}>Credit (Cr)</th>
                            <th style={{ padding: 12, textAlign: "right", fontSize: 13, color: C.muted }}>Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ledgerEntries.map((e, i) => (
                            <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                                <td style={{ padding: 12, fontSize: 13, color: C.muted }}>{formatDateDisplay(e.date)}</td>
                                <td style={{ padding: 12, fontSize: 13, color: C.text, fontWeight: 600 }}>{e.ref}</td>
                                <td style={{ padding: 12, fontSize: 14, color: C.text }}>{e.particulars}</td>
                                <td style={{ padding: 12, textAlign: "right", color: "#059669" }}>{e.dr > 0 ? `₹${e.dr.toLocaleString()}` : "-"}</td>
                                <td style={{ padding: 12, textAlign: "right", color: "#DC2626" }}>{e.cr > 0 ? `₹${e.cr.toLocaleString()}` : "-"}</td>
                                <td style={{ padding: 12, textAlign: "right", fontWeight: 700, color: C.text }}>₹{e.balance.toLocaleString()}</td>
                            </tr>
                        ))}
                        {ledgerEntries.length === 0 && <tr><td colSpan="6" style={{ padding: 40, textAlign: "center", color: C.muted }}>No transactions found for this account</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
      )}

      {/* Balance Sheet */}
      {tab === "balancesheet" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          {/* Liabilities (Left) */}
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: 20 }}>
                <h3 style={{ color: C.amber, marginBottom: 16 }}>Liabilities</h3>
                {balanceSheet.liabilities.map((l, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                        <span style={{ color: C.text }}>{l.name}</span>
                        <span style={{ fontWeight: 600 }}>₹{l.balance.toLocaleString()}</span>
                    </div>
                ))}
            </div>
            <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: 20 }}>
                <h3 style={{ color: C.text, marginBottom: 16 }}>Equity & Capital</h3>
                {balanceSheet.equity.map((e, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                        <span style={{ color: C.text }}>{e.name}</span>
                        <span style={{ fontWeight: 600 }}>₹{e.balance.toLocaleString()}</span>
                    </div>
                ))}
            </div>
          </div>

          {/* Assets (Right) */}
          <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: 20 }}>
            <h3 style={{ color: C.accent, marginBottom: 16 }}>Assets</h3>
            {balanceSheet.assets.map((a, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                    <span style={{ color: C.text }}>{a.name}</span>
                    <span style={{ fontWeight: 600 }}>₹{a.balance.toLocaleString()}</span>
                </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
